// src/services/authService.ts
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

// Types
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  accountType?: string;
  createdAt?: string;
  profilePictureUrl?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  company?: string;
}

// Auth service class
class AuthService {
  // Store tokens in localStorage
  private storeTokens(token: string): void {
    localStorage.setItem('accessToken', token);
  }

  // Get stored tokens
  public getTokens(): AuthTokens | null {
    const accessToken = localStorage.getItem('accessToken');
    
    if (accessToken) {
      return { accessToken };
    }
    
    return null;
  }

  // Clear tokens on logout
  private clearTokens(): void {
    localStorage.removeItem('accessToken');
  }

  // Login user
public async login(credentials: LoginCredentials): Promise<User> {
  try {
    const response = await axios.post(API_ENDPOINTS.LOGIN, credentials);
    const { user, token } = response.data;
    
    this.storeTokens(token);
    return user;
  } catch (error: any) {
    // Handle and standardize error format
    const errorMessage = error.response?.data?.error || 'Login failed. Please try again.';
    throw new Error(errorMessage);
  }
}

  // Register user
  public async register(userData: RegisterData): Promise<User> {
    try {
      const response = await axios.post(API_ENDPOINTS.REGISTER, userData);
      const { user, token } = response.data;
      
      this.storeTokens(token);
      return user;
    } catch (error) {
      throw error;
    }
  }

  // Logout user
  public logout(): void {
    this.clearTokens();
  }

  // Check if user is authenticated
  public isAuthenticated(): boolean {
    return !!this.getTokens()?.accessToken;
  }

  // Request password reset
  public async forgotPassword(email: string): Promise<void> {
    try {
      await axios.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });
    } catch (error) {
      throw error;
    }
  }

  // Reset password with token
  public async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await axios.post(API_ENDPOINTS.RESET_PASSWORD, {
        token,
        newPassword
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify a user's email by redeeming the single-use token from the
   * verification link. On success the backend returns a session JWT —
   * we persist it the same way login() does so the user lands signed in.
   *
   * Throws on any failure with the backend's error string so the page
   * can surface a precise reason (expired link, already used, etc.).
   */
  public async verifyEmail(token: string): Promise<User> {
    try {
      const response = await axios.post(API_ENDPOINTS.VERIFY_EMAIL, { token });
      const { user, token: jwt } = response.data || {};
      if (jwt) this.storeTokens(jwt);
      return user;
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Email verification failed.';
      const code = error?.response?.data?.code;
      const wrapped: Error & { code?: string } = new Error(msg);
      wrapped.code = code;
      throw wrapped;
    }
  }

  /**
   * Ask the backend to (re)issue + (re)send the verification email.
   * Backend rate-limits to ~60s between sends and intentionally returns
   * a generic 200 message regardless of whether the address is registered
   * — so we don't enumerate accounts. Surfacing the 429 cooldown is
   * the only failure mode we care about in the UI.
   */
  public async resendVerification(email: string): Promise<{ retryAfterSec?: number }> {
    try {
      await axios.post(API_ENDPOINTS.RESEND_VERIFICATION, { email });
      return {};
    } catch (error: any) {
      if (error?.response?.status === 429) {
        return { retryAfterSec: error.response.data?.retryAfterSec };
      }
      throw error;
    }
  }

  /**
   * Exchange a short-lived handoff token (minted by autopilot-backend
   * for a signed-in CLI user) for a full session JWT, and persist the
   * session JWT exactly the way `login()` does.
   *
   * Throws on any failure — callers should surface a friendly "couldn't
   * sign you in from the app" message rather than letting the user
   * proceed anonymously.
   */
  public async acceptHandoff(handoff: string): Promise<User> {
    const response = await axios.post(API_ENDPOINTS.HANDOFF_EXCHANGE, { handoff });
    const { user, token } = response.data || {};
    if (!token || !user) {
      throw new Error('Invalid handoff response from server');
    }
    this.storeTokens(token);
    return user;
  }

  // Refresh access token
  public async refreshToken(): Promise<string> {
    try {
      const tokens = this.getTokens();
      
      if (!tokens?.accessToken) {
        throw new Error('No access token available');
      }
      
      const response = await axios.post(API_ENDPOINTS.REFRESH_TOKEN, {
        token: tokens.accessToken
      });
      
      const newToken = response.data.token;
      this.storeTokens(newToken);
      
      return newToken;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }
}

// Create and export auth service instance
const authService = new AuthService();
export default authService;